import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, MapPin, PowerOff, Eye } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import SearchInput from "@/components/SearchInput";
import { useWithBack } from "@/lib/nav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntityCombobox } from "@/components/ui/EntityCombobox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function SitesList() {
  const withBackHref = useWithBack();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("site.write");

  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [customerFilter, setCustomerFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  // Monotonic request id — the newest fetch wins even if an older one is still in flight.
  const reqIdRef = useRef(0);

  useEffect(() => {
    api.get("/customers", { params: { active: true, limit: 500 } })
      .then(({ data }) => setCustomers(data.items))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const load = useCallback(async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (search) params.search = search;
      if (showInactive) params.active = "all";
      if (customerFilter !== "all") params.customer_id = customerFilter;
      const { data } = await api.get("/sites", { params });
      if (myReqId !== reqIdRef.current) return;
      setItems(data.items);
    } catch (err) {
      if (myReqId !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [search, showInactive, customerFilter]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const custMap = Object.fromEntries(customers.map((c) => [c.id, c.name]));

  const handleDeactivate = async (row) => {
    try {
      await api.delete(`/sites/${row.id}`);
      toast.success(`${row.name} deactivated`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="sites-page">
      <PageHeader
        testId="sites-header"
        title="Sites"
        subtitle="Physical locations where customer machines are installed."
        actions={canWrite && (
          <Button asChild size="sm" data-testid="site-new-button">
            <Link to="/sites/new"><Plus className="h-4 w-4" /> New Site</Link>
          </Button>
        )}
      />

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <SearchInput value={search} onChange={setSearch} placeholder="Search sites…" testId="sites-search" />
          <div className="w-56">
            <EntityCombobox
              items={customers}
              value={customerFilter === "all" ? "" : customerFilter}
              onChange={(v) => setCustomerFilter(v || "all")}
              placeholder="All customers"
              emptyMessage="No customers match."
              allowClear
              clearLabel="All customers"
              testid="sites-customer-filter"
              triggerClassName="h-8 text-xs"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Show inactive</Label>
          <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="sites-show-inactive" />
        </div>
      </div>

      <Card className="rounded-md overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="sites-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-3">Name</th>
                <th className="text-left py-2 px-3">Customer</th>
                <th className="text-left py-2 px-3">City</th>
                <th className="text-left py-2 px-3">Contact</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-xs text-muted-foreground" data-testid="sites-empty">No sites match.</td></tr>
              ) : items.map((s) => (
                <tr key={s.id} data-testid={`site-row-${s.id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="py-2 px-3">
                    <Link to={withBackHref(`/sites/${s.id}`)} className="font-medium hover:text-primary flex items-center gap-2" data-testid={`site-link-${s.id}`}>
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {s.name}
                    </Link>
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">{custMap[s.customer_id] || "—"}</td>
                  <td className="py-2 px-3 text-muted-foreground">{s.address?.city || "—"}</td>
                  <td className="py-2 px-3 text-muted-foreground">{s.on_site_contact_name || "—"}</td>
                  <td className="py-2 px-3">
                    {s.active ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="muted">Inactive</StatusBadge>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`site-actions-${s.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/sites/${s.id}`)} data-testid={`site-view-${s.id}`}><Eye className="h-4 w-4 mr-2" /> View</DropdownMenuItem>
                        {canWrite && <DropdownMenuItem onClick={() => navigate(`/sites/${s.id}/edit`)} data-testid={`site-edit-${s.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>}
                        {canWrite && s.active && (<>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleDeactivate(s)} data-testid={`site-deactivate-${s.id}`}><PowerOff className="h-4 w-4 mr-2" /> Deactivate</DropdownMenuItem>
                        </>)}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
